// src/components/IconButton.tsx
//
// ★★★ AN ICON-ONLY BUTTON, FOR THE ACTIONS WHOSE GLYPH IS UNIVERSAL.
//
// ══ WHEN TO USE THIS, AND WHEN NOT ══
//
// A screen full of identical outlined rectangles, each carrying a word, is slow to scan: the eye reads every label
// to find the one it wants. A small set of actions has a glyph nearly everyone already knows, and for those a
// square icon is found in one glance and takes a fraction of the width. This component is for exactly those:
//
//   USE AN ICON ALONE for secondary, repeatable, low-consequence actions with a universal glyph:
//     Print, Email, Refresh, First page, Last page, and a row's Remove (which still asks before it deletes).
//
//   KEEP THE WORDS for:
//     * a screen's primary action (Save, Calculate, Activate, Approve): the loudest control should say what it does;
//     * anything that changes what the system holds and is not trivially reversible (Retire, Reject, Rotate now);
//     * the two Download buttons, which keep icon AND words because requirement C-16 (P2C1 §12.52) says every
//       query must be downloadable in both formats, and an evaluator should find that in the words;
//     * an action whose glyph is not universal, where an icon would be a guess.
//
// ══ WHAT AN ICON ALONE MUST STILL HAVE (WCAG 2.1 AA) ══
//
//   1. **An accessible name.** The label is rendered as visually hidden text INSIDE the button, so a screen reader
//      announces "Refresh, button", the name is the control's own content (not an attribute that can drift), and a
//      test that looks for the button by its words still finds it.
//   2. **A visible name on demand.** A tooltip (CSS, `data-tip`) shows the same word on hover AND on keyboard focus.
//      It is decoration for sighted users; it is `aria-hidden` by construction (it lives in a pseudo-element, not in
//      the DOM), so it is never read twice.
//   3. **A target of at least 36 px square** (WCAG 2.5.8 asks for 24; this app's table sort buttons are 17 and the
//      touch-target review flagged them). A 16 px glyph in a 36 px box.
//   4. **The same focus ring as every other control**, from the global `:focus-visible`.
//
// ⚠ It is a NATIVE `<button>`, for the same reason `.pt-th-sort` is: `SgdsButton` renders its label through a slot
// and does not size a glyph-only child cleanly. Anything that is not a plain click (aria-expanded, aria-controls,
// aria-describedby, disabled) passes straight through.
import type { ButtonHTMLAttributes, ReactElement } from 'react'
import { Glyph, type GlyphName } from './icons'

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label' | 'title'> {
  /** Which glyph. */
  readonly glyph: GlyphName
  /** The control's NAME: what a screen reader says and what the tooltip shows. One or two words. */
  readonly label: string
  /** `danger` is for a destructive row action; everything else is `neutral`. */
  readonly tone?: 'neutral' | 'danger'
}

export function IconButton({ glyph, label, tone = 'neutral', className, type = 'button', ...rest }: IconButtonProps): ReactElement {
  return (
    <button
      type={type}
      className={['pt-icon-btn', tone === 'danger' ? 'pt-icon-btn--danger' : '', className ?? ''].filter((c) => c.length > 0).join(' ')}
      data-tip={label}
      {...rest}
    >
      <span aria-hidden="true" className="pt-icon-btn-glyph">
        <Glyph name={glyph} />
      </span>
      <span className="sr-only">{label}</span>
    </button>
  )
}
